/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, colorsV1, colorsV2 } from '@ajaibid/components';
import PinInput from 'react-pin-input';
import LoaderV2 from 'Component/Loader/V2';
import { noop } from '@ajaibid/utils';
import { PIN_INPUT_CLICK_TARGET, PIN_STATUS } from '@services/trackerConst';
import { FormProvider } from 'Component/FormV2';
import { useSelector } from 'react-redux';
import { Icon } from 'Component/Icon';
import { PinContext } from './context';
import { styBoxContainer, styContainer, styProfile, styButton, styProfilePicture } from './style';
import useEventTracker from './tracker';
import useCountdown from './hook/useCountdown';
import { PinResetSecurity } from './PinResetSecurity';

const NO_PROFILE_GRAY = require('Icon/MenuProfile/icon_no_profile.png');
const ENVELOPE = require('Image/img-mailbox.png');
const ARROW_LEFT = require('Icon/arrow-left.png');

export const InputPinBox = ({
  title,
  onComplete,
  onSuccess,
  onSuccessForgotPin,
  onError,
  isShowLogout,
  isLoadingTransaction,
  onBeforeLogout,
  withBackground,
  entryPoint,
}) => {
  const [pin, setPin] = useState('');
  const [lengthPin, setLengthPin] = useState(4);
  const [profilePicture, setProfilePicture] = useState(NO_PROFILE_GRAY);
  const [setMinutes, minutes, seconds, countdown] = useCountdown(0);
  const [isShowError, setIsShowError] = useState(false);
  const [step, setStep] = useState(0);

  const {
    eventClickForgotPin,
    eventVisitPinPage,
    eventSubmitPin,
    eventPinInputView,
    eventPinInputClick,
    eventPinVerifying,
  } = useEventTracker();
  const {
    forgetPinFlex,
    isPinError,
    isPinSuccess,
    validatePin,
    isLoadingPin,
    isPinDataLoading,
    isForgotPinSuccess,
    setIsForgotPinSuccess,
    pinData,
    pinDataResult,
    errorMessage,
    lockDuration,
    isIdAvailable,
    checkIdAvailibility,
  } = useContext(PinContext);

  useEffect(() => {
    pinData();
    checkIdAvailibility();
    eventVisitPinPage();
    // The same box also backs transaction and trusted-device prompts, which the PIN event
    // library does not cover — they pass no entryPoint and stay on the legacy events only.
    if (entryPoint) {
      eventPinInputView(entryPoint);
    }
  }, []);

  useEffect(() => {
    if (isPinSuccess) {
      onSuccess(pin);
      eventSubmitPin({
        'is success': 'true',
      });
      if (entryPoint) {
        eventPinVerifying(entryPoint, PIN_STATUS.SUCCESS);
      }
    }
  }, [isPinSuccess]);

  useEffect(() => {
    if (isPinError) {
      setIsShowError(true);
      onError(pin);
      eventSubmitPin({
        'is success': 'false',
        'error response': errorMessage,
      });
      if (entryPoint) {
        eventPinVerifying(entryPoint, PIN_STATUS.FAIL, errorMessage);
      }
    }
    if (isPinError && lockDuration >= 1) {
      setMinutes(lockDuration);
    } else {
      setMinutes(0);
    }
  }, [isPinError, errorMessage, lockDuration]);

  useEffect(() => {
    if (lockDuration > 0 && countdown < 1) {
      setIsShowError(false);
    }
  }, [countdown]);

  useEffect(() => {
    if (isForgotPinSuccess) {
      onSuccessForgotPin(pin);
      setStep(2);
      setIsForgotPinSuccess(false);
    }
  }, [isForgotPinSuccess]);

  useEffect(() => {
    if (pinDataResult?.is_pin_6digit === true) {
      setLengthPin(6);
    }
    if (pinDataResult?.profile_picture) {
      setProfilePicture(pinDataResult?.profile_picture);
    }
  }, [pinDataResult]);

  const featUsePinSecurityQuestion = useSelector(
    (state) => state.config?.configData?.forgot_pin_security_question === '1'
  );

  const handleForgotPin = () => {
    if (isIdAvailable && featUsePinSecurityQuestion) {
      setStep(1);
    } else {
      forgetPinFlex();
    }
    eventClickForgotPin();
    if (entryPoint) {
      eventPinInputClick(entryPoint, PIN_INPUT_CLICK_TARGET.FORGOT_PIN);
    }
  };

  const handleChangeAccount = () => {
    if (entryPoint) {
      eventPinInputClick(entryPoint, PIN_INPUT_CLICK_TARGET.CHANGE_ACCOUNT);
    }
    onBeforeLogout();
  };

  const handleStepBack = () => {
    if (isIdAvailable && featUsePinSecurityQuestion) {
      setStep(step - 1);
    } else {
      setStep(0);
    }
  };

  const RenderButtons = () => (
    <>
      <Button
        variant="naked"
        fluid
        className={styButton}
        onClick={handleForgotPin}
        data-testid="pin-forgot-pin"
      >
        Lupa PIN?
      </Button>
      {isShowLogout && (
        <Button
          variant="naked"
          fluid
          className={styButton}
          onClick={handleChangeAccount}
          data-testid="logout-button"
        >
          Ubah Akun?
        </Button>
      )}
    </>
  );

  const RenderTextError = () => {
    if (lockDuration && minutes >= 0) {
      return `PIN salah. Coba lagi dalam ${minutes}:${seconds} menit kedepan.`;
    }
    return errorMessage;
  };

  const RenderForm = () => (
    <>
      <Typography
        type="heading"
        size="sm"
        bold
        style={{ marginTop: '40px' }}
        data-testid="pin-title"
      >
        {title}
      </Typography>

      <div className={styProfile}>
        <div>
          <img
            src={profilePicture}
            alt="..."
            width="32px"
            height="32px"
            className={styProfilePicture}
          />
          {pinDataResult?.email && <span>{pinDataResult?.email}</span>}
        </div>
      </div>

      {isLoadingPin || isLoadingTransaction || isPinDataLoading || isPinSuccess ? (
        <div style={{ display: 'flex', justifyContent: 'center' }} data-testid="pin-loader">
          <LoaderV2 width="58px" height="58px" />
        </div>
      ) : (
        <>
          <PinInput
            length={lengthPin}
            secret
            type="numeric"
            inputMode="number"
            style={{ padding: '10px' }}
            inputStyle={{
              borderColor: isShowError ? colorsV2.R500 : colorsV2.N100,
              backgroundColor: colorsV1.primaryBackground,
              borderRadius: '4px',
              color: isShowError ? colorsV2.R500 : '#3A83F9',
              fontSize: '25px',
              margin: '0px 4px',
              paddingBottom: '2px',
            }}
            inputFocusStyle={{ borderColor: colorsV2.B400 }}
            onComplete={(value) => {
              onComplete(value);
              setPin(value);
              validatePin({ pin_code: value });
            }}
            disabled={countdown > 1}
            focus
            autoSelect
          />
          {isShowError && (
            <Typography
              type="label"
              size="md"
              style={{ color: colorsV2.R400, textAlign: 'center' }}
              data-testid="pin-error"
            >
              <RenderTextError />
            </Typography>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '54px' }}>
            <RenderButtons />
          </div>
        </>
      )}
    </>
  );

  const RenderForgotPinSuccess = () => (
    <div>
      <img src={ENVELOPE} alt="..." />
      <Typography
        type="heading"
        size="sm"
        bold
        style={{ marginTop: '40px' }}
        data-testid="forgot-pin-success"
      >
        Periksa email untuk ubah PIN
      </Typography>
      <Typography type="body" size="lg" style={{ marginTop: '16px' }}>
        Tautan untuk mengubah PIN telah kami kirim ke email terdaftar. Periksa folder spam jika
        diperlukan.
      </Typography>
    </div>
  );

  const RenderStep = () => {
    switch (step) {
      case 1:
        return (
          <FormProvider>
            <PinResetSecurity />
          </FormProvider>
        );
      case 2:
        return <RenderForgotPinSuccess />;
      default:
        return <RenderForm />;
    }
  };

  return (
    <div className={styContainer}>
      <div className={styBoxContainer(withBackground)}>
        {step >= 1 && (
          <span>
            <Button
              variant="naked"
              style={{ padding: '0', minWidth: '0' }}
              onClick={handleStepBack}
            >
              <Icon src={ARROW_LEFT} />
            </Button>
          </span>
        )}
        <RenderStep />
      </div>
    </div>
  );
};

InputPinBox.propTypes = {
  title: PropTypes.string,
  onComplete: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onSuccessForgotPin: PropTypes.func,
  onBeforeLogout: PropTypes.func,
  isShowLogout: PropTypes.bool,
  withBackground: PropTypes.bool,
  entryPoint: PropTypes.string,
  isLoadingTransaction: PropTypes.bool,
};

InputPinBox.defaultProps = {
  title: 'Masukkan PIN',
  isShowLogout: false,
  onBeforeLogout: noop,
  onSuccessForgotPin: noop,
  onError: noop,
  onComplete: noop,
  withBackground: true,
  isLoadingTransaction: false,
  entryPoint: null,
};
